import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    IconButton,
    Stack,
    Tooltip,
    Modal,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useDeficiencies } from '../../../Pages/Deficiencies/DeficiencyContext';
import { formatNotificationTime } from '../../../utils/dateUtils';

/**
 * CommentsSection Component
 * Displays comments for a deficiency and allows adding new comments
 *
 * @param {Object} props - Component props
 * @param {Object} props.deficiency - The deficiency object
 * @returns {React.ReactElement}
 */
const CommentsSection = ({ deficiency, onNavigateToAttempt }) => {
    const { state, fetchDeficiencyComments, addDeficiencyComment, getImageUrl } = useDeficiencies();
    const { comments, commentsLoading, commentsError } = state;

    const [newComment, setNewComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);

    const deficiencyComments = comments[deficiency?.deficiencyId] || [];

    // Fetch comments when deficiency changes
    useEffect(() => {
        if (deficiency?.deficiencyId && !comments[deficiency.deficiencyId]) {
            fetchDeficiencyComments(deficiency.deficiencyId);
        }
    }, [deficiency?.deficiencyId, comments, fetchDeficiencyComments]);

    const handleSubmitComment = useCallback(async () => {
        if ((!newComment.trim() && attachedFiles.length === 0) || !deficiency?.deficiencyId) return;

        setIsSubmitting(true);
        try {
            await addDeficiencyComment(deficiency.deficiencyId, newComment.trim(), attachedFiles);
            setNewComment('');
            setAttachedFiles([]);
        } catch (error) {
            // Error handled by UI via isSubmitting or other state if needed
        } finally {
            setIsSubmitting(false);
        }
    }, [newComment, attachedFiles, deficiency?.deficiencyId, addDeficiencyComment]);

    const handleFileSelect = useCallback((event) => {
        const newFiles = Array.from(event.target.files);
        setAttachedFiles((prev) => [...prev, ...newFiles].slice(0, 5));
        event.target.value = '';
    }, []);

    const handleRemoveFile = useCallback((index) => {
        setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleKeyPress = useCallback(
        (event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSubmitComment();
            }
        },
        [handleSubmitComment]
    );

    const formatCommentTime = useCallback((timestamp) => {
        return formatNotificationTime(timestamp);
    }, []);

    if (!deficiency) {
        return null;
    }

    return (
        <Box>
            <Typography
                variant="caption"
                gutterBottom
                sx={{
                    fontWeight: 600,
                    color: '#666666',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 1.5,
                    display: 'block',
                }}
            >
                Comments
            </Typography>

            {/* Error display */}
            {commentsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {commentsError}
                </Alert>
            )}

            {/* Comments list */}
            <Box sx={{ mb: 3 }}>
                {commentsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : deficiencyComments.length === 0 ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            fontStyle: 'italic',
                            textAlign: 'center',
                            py: 2,
                        }}
                    >
                        No comments yet.
                    </Typography>
                ) : (
                    <Stack spacing={2}>
                        {deficiencyComments.map((comment) => (
                            <Card
                                key={comment.commentId}
                                variant="outlined"
                                onClick={() => {
                                    if (comment.linkedAttemptId && onNavigateToAttempt) {
                                        onNavigateToAttempt(comment.linkedAttemptId);
                                    }
                                }}
                                role={
                                    comment.linkedAttemptId && onNavigateToAttempt
                                        ? 'button'
                                        : undefined
                                }
                                tabIndex={
                                    comment.linkedAttemptId && onNavigateToAttempt ? 0 : undefined
                                }
                                onKeyDown={(e) => {
                                    if (
                                        (e.key === 'Enter' || e.key === ' ') &&
                                        comment.linkedAttemptId &&
                                        onNavigateToAttempt
                                    ) {
                                        e.preventDefault();
                                        onNavigateToAttempt(comment.linkedAttemptId);
                                    }
                                }}
                                sx={{
                                    backgroundColor: comment.text?.startsWith('Rejection:')
                                        ? 'error.50'
                                        : 'background.paper',
                                    borderLeft: comment.text?.startsWith('Rejection:')
                                        ? '4px solid'
                                        : '1px solid',
                                    borderLeftColor: comment.text?.startsWith('Rejection:')
                                        ? 'error.main'
                                        : 'divider',
                                    cursor:
                                        comment.linkedAttemptId && onNavigateToAttempt
                                            ? 'pointer'
                                            : 'default',
                                    transition: 'all 0.2s ease',
                                    '&:hover':
                                        comment.linkedAttemptId && onNavigateToAttempt
                                            ? {
                                                  backgroundColor: comment.text?.startsWith(
                                                      'Rejection:'
                                                  )
                                                      ? 'error.100'
                                                      : 'action.hover',
                                                  transform: 'translateY(-1px)',
                                                  boxShadow: 1,
                                              }
                                            : {},
                                }}
                            >
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            mb: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: comment.text?.startsWith('Rejection:')
                                                    ? 'error.main'
                                                    : 'text.primary',
                                            }}
                                        >
                                            {comment.authorName}
                                            {comment.text?.startsWith('Rejection:') &&
                                                ' (Rejected)'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatCommentTime(comment.createdAt)}
                                        </Typography>
                                    </Box>
                                    {comment.text && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: 'pre-wrap',
                                                color: comment.text?.startsWith('Rejection:')
                                                    ? 'error.dark'
                                                    : 'text.primary',
                                            }}
                                        >
                                            {comment.text}
                                        </Typography>
                                    )}
                                    {comment.attachments &&
                                        comment.attachments.length > 0 &&
                                        (() => {
                                            const imageAtts = comment.attachments.filter(
                                                (att) =>
                                                    att.contentType &&
                                                    att.contentType.startsWith('image/')
                                            );
                                            const fileAtts = comment.attachments.filter(
                                                (att) =>
                                                    !att.contentType ||
                                                    !att.contentType.startsWith('image/')
                                            );
                                            return (
                                                <Box
                                                    sx={{
                                                        mt: 1,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1,
                                                    }}
                                                >
                                                    {imageAtts.length > 0 && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            {imageAtts.map((att) => {
                                                                const fileUrl = getImageUrl(
                                                                    att.fileUrl
                                                                );
                                                                return (
                                                                    <Box
                                                                        key={att.attachmentId}
                                                                        component="img"
                                                                        src={fileUrl}
                                                                        alt={att.fileName}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSelectedImage(
                                                                                fileUrl
                                                                            );
                                                                        }}
                                                                        sx={{
                                                                            width: 60,
                                                                            height: 60,
                                                                            objectFit: 'cover',
                                                                            borderRadius: 1,
                                                                            cursor: 'pointer',
                                                                            border: '1px solid',
                                                                            borderColor: 'divider',
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                    {fileAtts.length > 0 && (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 0.5,
                                                            }}
                                                        >
                                                            {fileAtts.map((att) => {
                                                                const fileUrl = getImageUrl(
                                                                    att.fileUrl
                                                                );
                                                                return (
                                                                    <Chip
                                                                        key={att.attachmentId}
                                                                        label={att.fileName}
                                                                        size="small"
                                                                        icon={
                                                                            <InsertDriveFileIcon fontSize="small" />
                                                                        }
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            window.open(
                                                                                fileUrl,
                                                                                '_blank'
                                                                            );
                                                                        }}
                                                                        clickable
                                                                        sx={{
                                                                            maxWidth: 200,
                                                                            width: 'fit-content',
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })()}
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Add comment form */}
            <Box>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={handleKeyPress}
                    disabled={isSubmitting}
                    sx={{
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'background.paper',
                        },
                    }}
                />
                {attachedFiles.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {attachedFiles.map((file, index) => (
                            <Chip
                                key={index}
                                label={file.name}
                                size="small"
                                onDelete={() => handleRemoveFile(index)}
                                icon={<InsertDriveFileIcon fontSize="small" />}
                                sx={{ maxWidth: 180 }}
                            />
                        ))}
                    </Box>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={attachedFiles.length >= 5 ? 'Max 5 files' : 'Attach files'}>
                            <span>
                                <IconButton
                                    component="label"
                                    disabled={isSubmitting || attachedFiles.length >= 5}
                                    size="small"
                                    sx={{
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&:focus-within': {
                                            outline: '2px solid #1652F0',
                                            outlineOffset: '2px',
                                        },
                                    }}
                                >
                                    <AttachFileIcon fontSize="small" />
                                    <input
                                        type="file"
                                        aria-label="Attach files to comment"
                                        multiple
                                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                                        onChange={handleFileSelect}
                                        disabled={isSubmitting || attachedFiles.length >= 5}
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            width: '100%',
                                            height: '100%',
                                            opacity: 0,
                                            cursor:
                                                isSubmitting || attachedFiles.length >= 5
                                                    ? 'default'
                                                    : 'pointer',
                                        }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                        {attachedFiles.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                {attachedFiles.length}/5
                            </Typography>
                        )}
                    </Box>
                    <Button
                        variant="contained"
                        onClick={handleSubmitComment}
                        disabled={
                            (!newComment.trim() && attachedFiles.length === 0) || isSubmitting
                        }
                        sx={{
                            minWidth: 100,
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Post Comment'
                        )}
                    </Button>
                </Box>
            </Box>

            {/* Image Modal */}
            <Modal
                open={!!selectedImage}
                onClose={() => setSelectedImage(null)}
                closeAfterTransition
                slotProps={{ backdrop: { timeout: 300 } }}
                sx={{ zIndex: 1300 }}
            >
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        outline: 'none',
                        p: 1,
                    }}
                >
                    <Box sx={{ position: 'relative' }}>
                        <IconButton
                            onClick={() => setSelectedImage(null)}
                            sx={{
                                position: 'absolute',
                                top: -40,
                                right: 0,
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        {selectedImage && (
                            <img
                                src={selectedImage}
                                alt="Full size"
                                style={{
                                    maxWidth: '95vw',
                                    maxHeight: '90vh',
                                    objectFit: 'contain',
                                    borderRadius: 4,
                                    boxShadow: '0 5px 20px rgba(0,0,0,0.2)',
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default CommentsSection;
